import { MxpBillingService } from '../../core/services/mxp-billing.service';
import { GenerateBillingRequest, GenerateBillingResponse, Tariff } from '../../core/models/mxp-billing.model';
import { MerchantService } from '../../core/services/mxp-merchant.service';
import { GetAllMerchantsResponse, MerchantResponse } from '../../core/models/mxp-merchant.model';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FloatValue, Timestamp } from '@ngx-grpc/well-known-types';
import { AuthService } from '@rezolved/auth';
import {
  BillingStatusEnum,
  MerchantTypeEnum,
  MakeQRPaymentRequestV4,
  PaymentSubtypeEnum,
  MxpPaymentGrpcClient,
  PaymentStatusEnum,
  PaymentTypeEnum,
} from '@rezolved/mxp-proto';
import * as _moment from 'moment';
import { Observable, Subscription, of, throwError, BehaviorSubject, timer } from 'rxjs';
import { filter, first, switchMap, take, tap } from 'rxjs/operators';
import { Auth0MxpUserRoles } from '../../core/constants/UserGroups';
import { MxpSnackBarService } from '../../core/services/mxp-snackbar.service';
import { FormControl } from '@angular/forms';
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { Moment } from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { PostpaymentTableData, PostpaymentTableService } from './postpayment-table.service';
import { BaseTableParams, Column, QueryData, BaseDataSource, ColumnTypeEnum, BaseTableComponent2 } from '@rezolved/ui';


const moment = _moment;
export const MY_FORMATS = {
  parse: {
    dateInput: 'MM/YYYY',
  },
  display: {
    dateInput: 'MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};


export interface SummaryDto {
  item: string;
  amount: number | undefined;
  isCurrency: boolean;
  displayBold: boolean
}

@Component({
  selector: 'mxp-billing',
  templateUrl: './billing.component.html',
  styleUrls: ['./billing.component.scss'],
  providers:[
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
    {provide: MAT_DATE_FORMATS, useValue: MY_FORMATS},
  ]
})
export class BillingComponent implements OnInit, OnDestroy {
  merchantId: string | undefined;
  private componentSubs = new Subscription();
  showSpinner = false;
  
  paymentsMade: number | undefined = 0;
  price: number | undefined = 0;
  totalPayments: number | undefined = 0;
  dateFromObj: Date = new Date();
  dateToObj: Date = new Date();
  dateFromUtc!: Date;
  dateToUtc!: Date;
  merchantsList: MerchantResponse[] = [];
  mxpUserRoles = Auth0MxpUserRoles;
  rolesWithAccessToPartnersDdl = [this.mxpUserRoles.MXP_SUPER_ADMIN];
  hasAccessToPartnerDdl = false;
  isFirstCall = false;
  roles: string[] = [];
  hasError = false;
  fromDate = new FormControl(_moment());
  toDate = new FormControl(_moment());
  invoicedAmount : number | undefined = 0.0;
  invoicedPaidAmount: number | undefined = 0.0;

  merchantType?: MerchantTypeEnum;
  billingStatus?: BillingStatusEnum;
  MerchantTypeEnum = MerchantTypeEnum;
  BillingStatusEnum = BillingStatusEnum;


  //Pricing Rules table
  pricinRulesDisplayedColumns: string[] = ['from', 'price'];
  tariff?: Tariff;
  INT_MAX_VALUE = 2147483647;
  
  //Summarytable
  summarydisplayedColumns: string[] = ['item', 'amount'];
  summaryInfo: SummaryDto[] = [];
  amountToPay = 0;

  //QR 
  qrAmount?: string;
  qrImgUrl?: string;

  //Payments table 
  dataSource: BaseDataSource<PostpaymentTableData>;
  defaultParams: BaseTableParams = {
    searchFilter: {},
    sortDirection: 'desc',
    pageIndex: 0,
    pageSize: 9,
    sortField: 'createdOn',
  };
  tableColumns: Column[] = [
    {
      key: 'timestamp',
      displayName: this.translate.instant('CREATED_ON'),
      type: ColumnTypeEnum.Text,
      sortField: 'createdOn',
      columnWidthPercentage: '10'
    },
    {
      key: 'interactedOnTimeStamp',
      displayName: this.translate.instant('INTERACTED_ON'),
      type: ColumnTypeEnum.Text,
      sortField: 'interactedOn',
      columnWidthPercentage: '8'
    },
    {
      key: 'description',
      displayName: this.translate.instant('DESCRIPTION'),
      type: ColumnTypeEnum.TextWithHover,
      sortField: 'description.keyword',
      columnWidthPercentage: '16'
    },
    {
      key: 'status',
      displayName: this.translate.instant('STATUS'),
      type: ColumnTypeEnum.Text,
      sortField: 'status',
      columnWidthPercentage: '10'
    },
    {
      key: 'total',
      displayName: this.translate.instant('TOTAL'),
      type: ColumnTypeEnum.Text,
      sortField: 'amount',
      columnWidthPercentage: '10'
    }
  ]

  query: BehaviorSubject<QueryData<PaymentStatusEnum[], PaymentTypeEnum, PaymentSubtypeEnum>> = new BehaviorSubject<QueryData<PaymentStatusEnum[], PaymentTypeEnum, PaymentSubtypeEnum>>({
    status: [
      PaymentStatusEnum.NOT_INTERACTED,
      PaymentStatusEnum.INTERIM,
      PaymentStatusEnum.PAYMENT_MADE,
      PaymentStatusEnum.REQUEST_POSTPONED,
      PaymentStatusEnum.REQUEST_REJECTED
    ],
    type: PaymentTypeEnum.ALL,
    subtype: PaymentSubtypeEnum.POSTPAYMENT,
    createdOnDate: {
      start: moment.utc('0001-01-01').toDate(),
      end: moment().endOf('day').toDate()
    },
    interactedOnDate: {
      start: moment.utc('0001-01-01').toDate(),
      end: moment().endOf('day').toDate()
    },
    additionalData: []
  });

  pageSizeOptions = [2, 5, 10, 20];

  //-------------------------

  constructor(
    private mxpBillingService: MxpBillingService,
    private merchantService: MerchantService,
    public authService: AuthService,
    private mxpSnackBarService: MxpSnackBarService,
    private translate: TranslateService,
    private mxpPaymentGrpcClient: MxpPaymentGrpcClient,
    private postpaymentTableService: PostpaymentTableService)
  {
    this.dataSource = new BaseDataSource<PostpaymentTableData>(this.postpaymentTableService);
  }

  ngOnInit(): void {
    this.initComponent();
  }

  refresh(): void {
    this.query.next({
      ...this.query.value
    });
  }

  private initComponent() {
    this.dataSource.loading$.subscribe(isLoading => {
      this.showSpinner = isLoading;
    });
    this.getCurrentMonthsDatesInitialized();
    this.showSpinner = true;
    this.hasError = false;
    this.componentSubs.add(
      this.authService.user$
        .pipe(
          filter((user) => !!user),
          take(1),
          tap((user) => {
            if (user) {
              this.merchantId = user['https://rezolve.com/mxp_merchant_id'];
              return;
            }
          }),
          switchMap(() => {
            return this.authService.roles$;
          }),
          take(1),
          switchMap((roles) => {
            if (roles) {
              if (this.rolesWithAccessToPartnersDdl.some((role) => roles.includes(role))) {
                this.hasAccessToPartnerDdl = true;
                this.isFirstCall = true;
                return this.merchantService.getAllMerchants().pipe(
                  switchMap((merchants: GetAllMerchantsResponse) => {
                    if (merchants && merchants.merchants && merchants.merchants.length > 0) {
                      this.merchantsList = merchants.merchants;
                      return of(null);
                    }
                    return throwError('No partners found');
                  }),
                );
              }
            }
            return of(null);
          }),
          switchMap(() => {
            return of(this.generateBilling()).pipe(
              switchMap((response) => {
                if (response === null) {
                  return of(null);
                }
                return response;
              })
            );
          })
        )
        .subscribe(
          (response: GenerateBillingResponse | null) => {
            if (response != null) {

              this.tariff = response.tariff ?? undefined;
              
              this.summaryInfo = [
                { item: this.translate.instant('NUMBER_OF_PAYMENTS'), amount: response.totalPayments, isCurrency: false, displayBold: false },
                { item: this.translate.instant('PAYMENTS_MADE'), amount: response.paymentMadePayments, isCurrency: false, displayBold: false  },
                { item: this.translate.instant('INVOICED_AMOUNT'), amount: response.invoicedAmount, isCurrency: true, displayBold: false  },
                { item: this.translate.instant('INVOICED_PAID_AMOUNT'), amount: response.invoicedPaidAmount, isCurrency: true, displayBold: false },
                { item: this.translate.instant('TOTAL_TO_PAY'), amount: response.price, isCurrency: true, displayBold: true }
              ];

              this.amountToPay = response.price as number;
              this.merchantType = response.merchantType;
              this.billingStatus = response.billingStatus ?? BillingStatusEnum.UNKNOWN;
              this.qrImgUrl = undefined;

              this.mxpSnackBarService.showSnackbarMessage('BILLING_GENERATED');
            }
          },
          (error): void => {
            console.log(error);
            this.showSpinner = false;
            this.hasError = true;
            this.mxpSnackBarService.showSnackbarMessage('BILLING_GENERATED_FAILED');
          },
          () => {
            this.showSpinner = false;
          }
        )
    );
  }

  getCurrentMonthsDatesInitialized() {
    this.dateFromObj = moment().startOf('month').toDate();
    this.dateToObj = moment().endOf('month').toDate();
    this.fromDate.setValue(this.dateFromObj);
    this.toDate.setValue(this.dateToObj);
  }

  handleMonthRangeSelected(data:any) {
    if (data !== undefined) {
      this.dateFromObj = data.startDate;
      this.dateToObj = data.endDate;
      this.handleGenerateBillingPromise();
    }
  }
  
  getMerchantsList() {
    this.merchantService.getAllMerchants().subscribe((response: GetAllMerchantsResponse) => {
      if (response) {
        this.merchantsList = response.merchants ?? [];
      }
    });
  }

  handleMerchantChange(merchantId: string) {
    this.showSpinner = true;
    this.isFirstCall = false;
    this.merchantId = merchantId;
    this.handleGenerateBillingPromise();
  }


  handleGenerateBillingPromise() {
    this.showSpinner = true;
    this.generateBilling().subscribe(
      (response: GenerateBillingResponse | null) => {
        if (response) {

          this.tariff = response.tariff ?? undefined;

          this.summaryInfo = [
            { item: this.translate.instant('NUMBER_OF_PAYMENTS'), amount: response.totalPayments, isCurrency: false, displayBold: false },
            { item: this.translate.instant('PAYMENTS_MADE'), amount: response.paymentMadePayments, isCurrency: false, displayBold: false },
            { item: this.translate.instant('INVOICED_AMOUNT'), amount: response.invoicedAmount, isCurrency: true, displayBold: false },
            { item: this.translate.instant('INVOICED_PAID_AMOUNT'), amount: response.invoicedPaidAmount, isCurrency: true, displayBold: false },
            { item: this.translate.instant('TOTAL_TO_PAY'), amount: response.price, isCurrency: true, displayBold: true }
          ];

          this.amountToPay = response.price as number;
          this.merchantType = response.merchantType;
          this.billingStatus = response.billingStatus ?? BillingStatusEnum.UNKNOWN;
          this.qrImgUrl = undefined;
          this.mxpSnackBarService.showSnackbarMessage('BILLING_GENERATED');  
        }
    },
    (error) => {
      console.log(error);
      this.showSpinner = false
    },
    () => (this.showSpinner = false)
    );
  }

  getBillingStatusLabel(): string {
    if (this.billingStatus === undefined) {
      return '';
    }
    const translationKey = `BILLING_STATUS.${BillingStatusEnum[this.billingStatus]}`;
    return this.translate.instant(translationKey);
  }

  generateBilling(): Observable<GenerateBillingResponse | null> {

    if (!this.isFirstCall) {

      this.dateFromUtc = new Date(Date.UTC(this.dateFromObj.getFullYear(), this.dateFromObj.getMonth(), this.dateFromObj.getDate(), 0, 0, 0));
      this.dateToUtc = new Date(Date.UTC(this.dateToObj.getFullYear(),this.dateToObj.getMonth(),this.dateToObj.getDate(),23, 59, 59));

      const generateBillingRequest: GenerateBillingRequest = {
        dateFrom: this.dateFromUtc.toISOString(),
        dateTo: this.dateToUtc.toISOString(),
        merchantId: this.merchantId
      };
      return this.mxpBillingService.generateBilling(generateBillingRequest);
    }
    else
      return of(null);
  }

  onPay(amountToPay: number): void {
    this.showSpinner = true;
    this.authService.user$.pipe(first()).subscribe((user) => {
      if (user) {        
        const request = new MakeQRPaymentRequestV4({
          description: this.translate.instant('POSTPAYMENT_QR_DESCRIPTION') + amountToPay,
          reference: 0,
          extendedReference: undefined,
          expiryDate: undefined,
          contactInfo: undefined,
          amount: { value: amountToPay } as FloatValue,
          paymentSubtype: PaymentSubtypeEnum.POSTPAYMENT,
          postpaymentPeriodFrom: Timestamp.fromDate(this.dateFromUtc),
          postpaymentPeriodTo: Timestamp.fromDate(this.dateToUtc)
        });
        this.mxpSnackBarService.wrapApiCall('SNACKBAR.MAKE_QR_PAYMENT', this.mxpPaymentGrpcClient.makeQRPaymentV4(request)).subscribe((response) => {
          this.qrAmount = amountToPay.toString();
          this.qrImgUrl = `data:image/png;base64,${response.success?.qrData?.value}`;

          timer(500).subscribe(() => {
            this.refresh();
          });
        },
          (error) => this.showSpinner = false,
          () => this.showSpinner = false
        )
      }
    });
  }

  ngOnDestroy() {
    this.componentSubs.unsubscribe();
  }
}
